import React from "react";
import type { ArrowPath } from "../computeArrows";

interface Props {
  arrows: ArrowPath[];
  width: number;
  height: number;
  selectedId: string | null;
  /** Draw ONLY the binding (driving) dependency lines; hide slack preds. */
  drivingOnly: boolean;
}

// In-section dependency lines are a clear cool gray; cross-scope (external /
// finished-work driver) lines are TEAL + dashed — distinct from the orange data
// date and the green bars — so "internal logic" vs "external feed" reads at a
// glance and you can trace which line goes to which marker.
const ARROW = "#9aa6b8";
const ARROW_CROSS = "#46b3c9";

const ArrowHead: React.FC<{ id: string; fill: string }> = ({ id, fill }) => (
  <marker
    id={id}
    viewBox="0 0 10 10"
    refX="9"
    refY="5"
    markerWidth="5"
    markerHeight="5"
    orient="auto-start-reverse"
  >
    <path d="M 0 0 L 10 5 L 0 10 z" fill={fill} />
  </marker>
);

const DepArrows: React.FC<Props> = ({ arrows, width, height, selectedId, drivingOnly }) => (
  <svg
    className="dep-strip-arrows"
    width={width}
    height={height}
    style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
  >
    <defs>
      <ArrowHead id="dep-arrow-head" fill={ARROW} />
      <ArrowHead id="dep-arrow-head--cross" fill={ARROW_CROSS} />
      <ArrowHead id="dep-arrow-head--active" fill="var(--accent)" />
    </defs>
    {arrows.map((a) => {
      if (drivingOnly && !a.driving) return null;
      const isConnected =
        !!selectedId && (a.predId === selectedId || a.succId === selectedId);
      const isDimmed = !!selectedId && !isConnected;
      // The BINDING (driving) line reads solid + full strength; a slack pred line
      // is thinner + faint so the line that actually sets the date stands out.
      const slack = !a.driving;
      const marker = isConnected ? "--active" : a.crosses ? "--cross" : "";
      return (
        <path
          key={a.arrowId}
          d={a.d}
          fill="none"
          stroke={isConnected ? "var(--accent)" : a.crosses ? ARROW_CROSS : ARROW}
          strokeWidth={isConnected ? 1.75 : slack ? 1 : 1.4}
          strokeDasharray={a.crosses ? "5 3" : undefined}
          opacity={isDimmed ? 0.3 : slack ? 0.4 : 1}
          markerEnd={`url(#dep-arrow-head${marker})`}
        />
      );
    })}
  </svg>
);

export default React.memo(DepArrows);
